<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Listas de Precios</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button type="button" class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters">
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>
              <div class="d-md-none">
                <button type="button" class="button-tooltip text-dark font-weight-bold m-0" v-b-tooltip.hover
                  title="Recargar" @click="loadData()">
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover
                title="Recargar" @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>
            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar placeholder="Buscar por nombre o código" @input="search = $event" class="flex-1" />
              <div class="text-right mt-2 mt-md-0">
                <b-button :to="{ name: 'tarifas-price-type-create' }" variant="none"
                  class="button button-primary ml-md-2">Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu :show="showFilters" @hide="showFilters = false" :options="filterOptions"
            :selectedOptionFilter="filters" @setFilters="setFilters"></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button type="button" class="text-white p-0 bg-transparent border-0 ml-2" @click="deleteFilter(name)">
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Código</th>
              <th>Tipo</th>
              <th>Fórmula</th>
              <th class="text-center">Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in filteredList" :key="o.id">
              <td>
                {{ o.name }}
                <i v-if="o.is_protected" class="ri-lock-line text-muted ml-1"
                  v-b-tooltip.hover title="Lista protegida — no se puede eliminar ni suspender"></i>
              </td>
              <td><code>{{ o.code }}</code></td>
              <td>{{ o.calculation_type === 'dynamic' ? 'Dinámico' : 'Fijo' }}</td>
              <td>
                <code v-if="o.formula">{{ o.formula }}</code>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <span :class="`badge ${o.is_active ? 'badge-success' : 'badge-danger'}`">
                  {{ o.is_active ? 'Activo' : 'Suspendido' }}
                </span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Editar"
                      @click="$router.push({ name: 'tarifas-price-type-edit', params: { id: o.id } })" />
                    <DropdownItem v-if="!o.is_protected"
                      :text="o.is_active ? 'Suspender' : 'Reactivar'"
                      @click="openChangeStatus(o)" />
                    <DropdownItem v-if="!o.is_protected"
                      text="Eliminar"
                      @click="openDelete(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <ConfirmationModal :show="confirmationModal.show" @hide="closeConfirmationModal"
      :data="confirmationModal" @confirm="confirmModal" />
  </div>
</template>

<script>
import { PriceTypeService } from '@/core/services'
import {
  Alert,
  Constants,
  DropdownTable,
  DropdownItem,
  SearchBar,
  FilterMenu,
  FilterMixin,
  ConfirmationModal,
  ConfirmationModalMixin,
} from 'wize-admin'

export default {
  components: { SearchBar, FilterMenu, DropdownTable, DropdownItem, ConfirmationModal },
  mixins: [FilterMixin, ConfirmationModalMixin],
  data() {
    return {
      list: [],
      search: '',
      filterOptions: [
        {
          label: 'Tipo',
          key: 'calculation_type',
          type: Constants.TypeFilters.SINGLE,
          options: [
            { key: 'dynamic', label: 'Dinámico' },
            { key: 'fixed', label: 'Fijo' },
          ],
        },
        {
          label: 'Estado',
          key: 'is_active',
          type: Constants.TypeFilters.SINGLE,
          options: [
            { key: true, label: 'Activo' },
            { key: false, label: 'Suspendido' },
          ],
        },
      ],
    }
  },
  computed: {
    filteredList() {
      const q = this.search.toLowerCase()
      return this.list.filter(o => {
        const matchSearch = !q || o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
        const matchType = this.filters.calculation_type == null || o.calculation_type === this.filters.calculation_type
        const matchStatus = this.filters.is_active == null || o.is_active === this.filters.is_active
        return matchSearch && matchType && matchStatus
      })
    },
  },
  methods: {
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmationModal({
        title: o.is_active ? 'Suspender lista' : 'Reactivar lista',
        description: `¿Está seguro que desea ${o.is_active ? 'suspender' : 'reactivar'} la lista '${o.name}'?`,
        acceptText: 'Aceptar',
        action: 'update',
      })
    },
    openDelete(o) {
      this.selectedOption = o
      this.openConfirmationModal({
        title: 'Eliminar lista',
        description: `¿Está seguro que desea eliminar la lista '${o.name}'? Esta acción no se puede deshacer.`,
        acceptText: 'Eliminar',
        action: 'delete',
      })
    },
    confirmModal() {
      this.closeConfirmationModal()
      if (this.confirmationModal.action === 'update') this.changeStatus()
      if (this.confirmationModal.action === 'delete') this.deletePriceType()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        await PriceTypeService.changeStatus(this.selectedOption.id, !this.selectedOption.is_active)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors?.message || 'Error al actualizar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async deletePriceType() {
      try {
        await this.$store.dispatch('app/loading', true)
        await PriceTypeService.remove(this.selectedOption.id)
        await this.loadData()
        Alert.success('Lista eliminada correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors?.message || 'Error al eliminar la lista')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await PriceTypeService.list()
        this.list = res.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar las listas de precios')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="stylus" scoped></style>
